import { api } from "./client";
import type { ProjectDeployment } from "@mycompbox/shared";
import type { Job } from "./mailbox";

export type MergeMethod = "squash" | "merge" | "rebase";
export type DeployEnvironment = "dev" | "stage" | "prod";
export type PullRequestState = "open" | "closed" | "merged";

/** ENG-296: the most a person can write when sending a pull request back to the agent. */
export const SEND_BACK_NOTES_MAX_LENGTH = 5000;

export interface PullRequestFile {
  path: string;
  previousPath: string | null;
  /** added, modified, removed, renamed, copied, changed, unchanged */
  status: string;
  additions: number;
  deletions: number;
  /** Missing for binary files and diffs GitHub won't show. */
  patch: string | null;
  /** True when the patch was cut short. */
  truncated: boolean;
}

export interface ChecksSummary {
  total: number;
  passed: number;
  failed: number;
  pending: number;
}

export interface AgentReviewRisk {
  severity: "low" | "medium" | "high";
  file?: string | null;
  line?: number | null;
  explanation: string;
}

export interface AgentReviewBody {
  summary: string;
  changes: string[];
  risks: AgentReviewRisk[];
  testResults: string;
  recommendation: "approve" | "needs_changes" | "needs_human";
  reasons: string[];
  truncatedFiles?: string[];
  excludedFiles?: string[];
}

export interface AgentReviewEnvelope {
  /** The stored review: normally {@link AgentReviewBody}, plain text for legacy rows. */
  review: unknown;
  sha: string | null;
  model: string | null;
  reviewedAt: string | null;
  /** True when new commits landed after the review was written. */
  stale: boolean;
}

/** A deploy started from this pull request (ENG-297). */
export interface PullRequestDeployment {
  id: string;
  environment: string;
  sha: string;
  requestedBy: string;
  requestedAt: string;
}

export interface PullRequestView {
  id: string;
  /** The project's team id. */
  projectId: string | null;
  jobId: string | null;
  epicRequestId: string | null;
  repoFullName: string;
  number: number;
  title: string;
  state: PullRequestState;
  htmlUrl: string;
  headSha: string;
  baseSha: string;
  headRef: string;
  baseRef: string;
  /** Null while GitHub is still working it out. */
  mergeable: boolean | null;
  /** GitHub's mergeable_state: clean, blocked, dirty, behind, unstable, unknown, draft... */
  mergeableState: string | null;
  /** ER-130: a draft can't be merged until it's marked ready. Absent on older servers. */
  draft?: boolean;
  mergedSha: string | null;
  mergedAt: string | null;
  checks: ChecksSummary | null;
  allowedMergeMethods: MergeMethod[];
  migrationFiles: string[];
  /** ENG-419: each changed migration classified as additive or destructive. Absent on older servers. */
  migrations?: ClassifiedMigration[];
  /** ENG-419: the owner's approval of destructive migrations; `current` is true only for the live head. */
  destructiveApproval?: DestructiveApproval | null;
  /** ENG-419: whether the caller may approve destructive migrations. */
  canApproveDestructive?: boolean;
  /** ENG-419: the project applies migrations automatically on merge. Absent on older servers. */
  autoApplyMigrations?: boolean;
  files: PullRequestFile[];
  filesPage: { page: number; pageSize: number; total: number; hasMore: boolean };
  agentReview: AgentReviewEnvelope | null;
  deployments: PullRequestDeployment[];
  /** Send-back / resolve-conflicts runs started from this pull request, oldest first. */
  fixJobIds: string[];
  canDeployProd: boolean;
  /** ENG-421: the newest deploy records linked to this pull request, newest first. Absent on older servers. */
  deployRecords?: DeployRecordView[];
}

/**
 * ENG-421: one deploy of a built commit, as the API returns it
 * (mirrors toDeployRecordView in packages/api/src/services/deployRecordService.ts).
 */
export interface DeployRecordView {
  id: string;
  commitSha: string;
  buildId: string;
  status: string;
  backupObject: string | null;
  appliedMigrations: string[];
  pullRequestReviewId: string | null;
  epicId: string | null;
  organizationId: string | null;
  createdAt: string;
}

/** ER-130: one of an epic's pull requests, in the order to merge them. */
export interface EpicPullRequest {
  /** The CompBox review page (/pull-requests/:id); null when GitHub isn't connected through the app. */
  id: string | null;
  repoFullName: string;
  number: number;
  title: string | null;
  htmlUrl: string;
  /** The PR's branch; null when only the run's recorded url is known. */
  headRef?: string | null;
  state: PullRequestState;
  draft: boolean;
  mergeableState: string | null;
  checks: ChecksSummary | null;
  baseRef: string | null;
  mergedSha: string | null;
  mergedAt: string | null;
  jobId: string;
  openedAt: string;
}

/** ENG-419: how a changed migration was classified. */
export interface ClassifiedMigration {
  path: string;
  safety: "additive" | "destructive";
  reasons: string[];
}

/** ENG-419: the owner approval of a pull request's destructive migrations. */
export interface DestructiveApproval {
  /** The approver's user id. */
  approvedBy: string;
  approvedAt: string;
  sha: string;
  current: boolean;
}

export type FixJobTarget = { runsIn: "machine"; machineId: string; useAiKey?: boolean } | { runsIn: "cloud" };

export interface MergeInput {
  method: MergeMethod;
  expectedHeadSha: string;
  confirmMigrationsApplied?: boolean;
  /** Records that the live database was backed up for the current head. Required when a migration can lose data. */
  confirmBackupTaken?: boolean;
}

export interface DeployResult {
  deployment: ProjectDeployment;
  link: PullRequestDeployment;
  deployments: PullRequestDeployment[];
}

/** Narrows a stored review to the structured shape, or null for plain-text/legacy rows. */
export function structuredAgentReview(review: unknown): AgentReviewBody | null {
  if (typeof review !== "object" || review === null) return null;
  const r = review as Partial<AgentReviewBody>;
  if (typeof r.summary !== "string" || typeof r.recommendation !== "string") return null;
  return {
    summary: r.summary,
    changes: Array.isArray(r.changes) ? r.changes : [],
    risks: Array.isArray(r.risks) ? r.risks : [],
    testResults: typeof r.testResults === "string" ? r.testResults : "",
    recommendation: r.recommendation,
    reasons: Array.isArray(r.reasons) ? r.reasons : [],
    truncatedFiles: Array.isArray(r.truncatedFiles) ? r.truncatedFiles : [],
    excludedFiles: Array.isArray(r.excludedFiles) ? r.excludedFiles : [],
  };
}

const pageQuery = (page: number) => (page > 1 ? `?page=${String(page)}` : "");

export const pullRequestApi = {
  get: (id: string, page = 1) =>
    api.get<{ data: PullRequestView }>(`/pull-requests/${encodeURIComponent(id)}${pageQuery(page)}`).then((r) => r.data),
  byJob: (jobId: string) =>
    api.get<{ data: PullRequestView }>(`/pull-requests/by-job/${encodeURIComponent(jobId)}`).then((r) => r.data),
  byEpicRequest: (epicRequestId: string) =>
    api
      .get<{ data: PullRequestView }>(`/pull-requests/by-epic-request/${encodeURIComponent(epicRequestId)}`)
      .then((r) => r.data),
  byEpic: (epicId: string) =>
    api.get<{ data: EpicPullRequest[] }>(`/pull-requests/by-epic/${encodeURIComponent(epicId)}`).then((r) => r.data),
  markReady: (id: string) =>
    api
      .post<{ data: { id: string; number: number; draft: boolean } }>(`/pull-requests/${encodeURIComponent(id)}/ready`, {})
      .then((r) => r.data),
  merge: (id: string, input: MergeInput) =>
    api
      .post<{ data: { merged: true; sha: string } }>(`/pull-requests/${encodeURIComponent(id)}/merge`, input)
      .then((r) => r.data),
  approveDestructiveMigrations: (id: string, input: { expectedHeadSha: string }) =>
    api
      .post<{ data: { id: string; destructiveApproval: DestructiveApproval | null } }>(
        `/pull-requests/${encodeURIComponent(id)}/approve-destructive-migrations`,
        input
      )
      .then((r) => r.data),
  review: (id: string) =>
    api
      .post<{ data: { id: string; headSha: string; agentReview: AgentReviewEnvelope | null } }>(
        `/pull-requests/${encodeURIComponent(id)}/review`,
        {}
      )
      .then((r) => r.data),
  sendBack: (id: string, input: { notes: string; target: FixJobTarget }) =>
    api.post<{ data: Job }>(`/pull-requests/${encodeURIComponent(id)}/send-back`, input).then((r) => r.data),
  resolveConflicts: (id: string, input: { target: FixJobTarget }) =>
    api
      .post<{ data: Job }>(`/pull-requests/${encodeURIComponent(id)}/resolve-conflicts`, input)
      .then((r) => r.data),
  deploy: (id: string, input: { environment: DeployEnvironment; confirm?: boolean }) =>
    api.post<{ data: DeployResult }>(`/pull-requests/${encodeURIComponent(id)}/deploy`, input).then((r) => r.data),
};
